import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/axios";
import wsClient from "../utils/websocket";
import "./AnnouncementBar.css";

const ANNOUNCEMENT_BAR_HEIGHT = "36px";

function isTickerPayload(data) {
  return (
    data &&
    (data.type === "ticker_show" || data.type === "ticker_refresh") &&
    typeof data.text === "string"
  );
}

function AnnouncementBar({ onOffsetChange }) {
  const [activeMessage, setActiveMessage] = useState(null);
  const [animationKey, setAnimationKey] = useState(0);
  const hideTimerRef = useRef(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const hideBar = useCallback(() => {
    clearHideTimer();
    setActiveMessage(null);
  }, [clearHideTimer]);

  const showMessage = useCallback(
    (detail, restartAnimation = true) => {
      if (!detail?.text) return;

      const loopCount = Math.max(1, Number(detail.loop_count) || 1);
      const loopDurationSeconds = Math.max(
        1,
        Number(detail.loop_duration_seconds) || 30,
      );

      setActiveMessage({
        id: detail.id,
        text: detail.text,
        loopCount,
        loopDurationSeconds,
      });

      if (restartAnimation) {
        setAnimationKey((key) => key + 1);
      }

      clearHideTimer();
      hideTimerRef.current = setTimeout(
        hideBar,
        loopCount * loopDurationSeconds * 1000,
      );
    },
    [clearHideTimer, hideBar],
  );

  const handleTickerPayload = useCallback(
    (payload) => {
      if (!isTickerPayload(payload)) return;
      showMessage(payload, payload.type !== "ticker_refresh");
    },
    [showMessage],
  );

  useEffect(() => {
    const onShow = (event) => handleTickerPayload(event.detail);
    const onRefresh = (event) => {
      if (isTickerPayload(event.detail)) {
        showMessage(event.detail, true);
      }
    };

    window.addEventListener("ticker_show", onShow);
    window.addEventListener("ticker_refresh", onRefresh);

    return () => {
      window.removeEventListener("ticker_show", onShow);
      window.removeEventListener("ticker_refresh", onRefresh);
      clearHideTimer();
    };
  }, [handleTickerPayload, showMessage, clearHideTimer]);

  useEffect(() => {
    const handleWsMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleTickerPayload(data);
      } catch {
        // ignorieren
      }
    };

    const attachListener = () => {
      if (wsClient.ws) {
        wsClient.ws.addEventListener("message", handleWsMessage);
      }
    };

    attachListener();
    const retry = setInterval(attachListener, 1000);

    return () => {
      clearInterval(retry);
      if (wsClient.ws) {
        wsClient.ws.removeEventListener("message", handleWsMessage);
      }
    };
  }, [handleTickerPayload]);

  useEffect(() => {
    let cancelled = false;

    const loadActiveTicker = async () => {
      try {
        const { data } = await api.get("/ticker/active");
        if (!cancelled && data?.active) {
          showMessage(data.active, true);
        }
      } catch {
        // kein aktiver Lauftext oder API nicht verfügbar
      }
    };

    loadActiveTicker();

    return () => {
      cancelled = true;
    };
  }, [showMessage]);

  useEffect(() => {
    const root = document.documentElement;
    const offsetPx = activeMessage ? 36 : 0;

    root.style.setProperty(
      "--app-announcement-height",
      activeMessage ? ANNOUNCEMENT_BAR_HEIGHT : "0px",
    );
    onOffsetChange?.(offsetPx);
  }, [activeMessage, onOffsetChange]);

  useEffect(
    () => () => {
      document.documentElement.style.setProperty(
        "--app-announcement-height",
        "0px",
      );
      onOffsetChange?.(0);
    },
    [onOffsetChange],
  );

  if (!activeMessage) {
    return null;
  }

  const animationDuration = activeMessage.loopDurationSeconds;

  return (
    <div className="announcement-bar" role="status" aria-live="polite">
      <div className="announcement-bar__viewport">
        <div
          key={animationKey}
          className="announcement-bar__track"
          style={{
            animationDuration: `${animationDuration}s`,
            animationIterationCount: activeMessage.loopCount,
          }}
        >
          <span className="announcement-bar__text">{activeMessage.text}</span>
        </div>
      </div>
    </div>
  );
}

export default AnnouncementBar;
